import {Button} from '~/components';
import {Text} from '../elements/Text';
import {useState} from 'react';
import {HeroOverlay} from './HeroOverlay.client';
import {TransitionVisible} from './TransitionVisible';
import {FiftyFifty} from '~/types/FiftyFiftyEntity';
import clsx from 'clsx';
import {SpreadMedia} from '../media/SpreadMedia';
import {Link} from '@shopify/hydrogen';
import {DATA_TEST_IDS} from '~/lib/const';

export function FiftyFiftyItem({fiftyFifty}: {fiftyFifty: FiftyFifty}) {
  const [overlayVisible, setOverlayVisible] = useState(false);

  const link = fiftyFifty.fiftyFiftyContentBlock?.buttons?.[0]?.buttonHref;

  return (
    <div
      data-testid={DATA_TEST_IDS.FIFTY_FIFTY_ITEM}
      className="relative w-full lg:w-1/2 aspect-square"
    >
      <LinkComponent to={link}>
        <SpreadMedia
          media={fiftyFifty}
          classNameVideo="aspect-square"
          breakpoints={[
            [360, 360],
            [670, 670],
          ]}
        />
      </LinkComponent>

      <TransitionVisible show={!overlayVisible}>
        <div className="flex flex-col items-start absolute left-[41px] lg:left-[50px] right-[41px] lg:right-[50px] bottom-[34px] lg:bottom-[44px] z-[1px]">
          {fiftyFifty.fiftyFiftyContentBlock?.title && (
            <LinkComponent to={link}>
              <div
                className={clsx('mt-5 p-2 rounded-lg max-w-[748px]', {
                  'text-white':
                    fiftyFifty.fiftyFiftyContentBlock?.textColor === 'white',
                  'text-black':
                    fiftyFifty.fiftyFiftyContentBlock?.textColor === 'black',
                  'backdrop-blur-md':
                    (fiftyFifty.fiftyFiftyContentBlock.backgroundOpacity ?? 0) >
                    0,
                })}
                style={{
                  backgroundColor:
                    fiftyFifty.fiftyFiftyContentBlock?.backgroundColor ===
                    'white'
                      ? `rgba(255,255,255,${
                          (fiftyFifty.fiftyFiftyContentBlock
                            .backgroundOpacity ?? 0) / 100
                        })`
                      : fiftyFifty.fiftyFiftyContentBlock?.backgroundColor ===
                        'black'
                      ? `rgba(0,0,0,${
                          (fiftyFifty.fiftyFiftyContentBlock
                            .backgroundOpacity ?? 0) / 100
                        })`
                      : 'transparent',
                }}
              >
                {fiftyFifty.fiftyFiftyContentBlock?.title && (
                  <Text
                    testid={DATA_TEST_IDS.FIFTY_FIFTY_TITLE}
                    as="h2"
                    className="line-clamp-2"
                  >
                    {fiftyFifty.fiftyFiftyContentBlock?.title.toUpperCase()}
                  </Text>
                )}

                {fiftyFifty.fiftyFiftyContentBlock?.description && (
                  <div className="mt-5 line-clamp-2 w-3/4">
                    <Text testid={DATA_TEST_IDS.FIFTY_FIFTY_DESCRIPTION}>
                      {fiftyFifty.fiftyFiftyContentBlock?.description}
                    </Text>
                  </div>
                )}
              </div>
            </LinkComponent>
          )}
          <div className="space-x-[30px]">
            {fiftyFifty.fiftyFiftyContentBlock?.buttons?.map((button) => {
              if (!button.buttonText) {
                return null;
              }

              return (
                <Button
                  data-testid={DATA_TEST_IDS.BUTTON('fifty')}
                  maxWidth={250}
                  to={button.buttonHref}
                  key={button.buttonText}
                  variant={button.buttonType}
                  className="mt-5 lg:mt-[30px] md:mt-9 truncate"
                >
                  {button.buttonText.toUpperCase()}
                </Button>
              );
            })}
          </div>
        </div>
      </TransitionVisible>

      {fiftyFifty.heroOverlay && fiftyFifty.heroOverlay.heroOverlayDisplay && (
        <HeroOverlay
          testid={DATA_TEST_IDS.OVERLAY('fifty')}
          title={fiftyFifty.heroOverlay?.copy}
          visible={overlayVisible}
          type="fiftyFifty"
          setVisible={setOverlayVisible}
        />
      )}
    </div>
  );
}

function LinkComponent(props: {to?: string; children: React.ReactNode}) {
  if (props.to) {
    return <Link to={props.to}>{props.children}</Link>;
  }

  return <div>{props.children}</div>;
}
